import React from "react";
export function useSectionScroll() {
  const [activeSectionId, setActiveSectionId] = React.useState("");
  const scrolling = React.useRef(false);
  const scrollTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const [sections, setSections] = React.useState<HTMLElement[]>([]);
  const timeout = React.useRef<NodeJS.Timeout | null>(null);
  React.useEffect(() => {
    timeout.current = setTimeout(() => {
      const sections = Array.from(document.querySelectorAll<HTMLElement>("section[id]"));
      setSections(sections);
    }, 250);
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);
  React.useEffect(() => {
    if (sections.length === 0) {
      return;
    }

    // Get the parent of all sections
    const sectionsParent = sections[0]?.parentElement;
    // Find the last element in the parent of all sections
    const lastElementInSectionsParent = sectionsParent?.lastElementChild;
    function handleScroll() {
      if (scrolling.current) {
        return;
      }
      let mostVisibleAnchor: string | null = null;
      let maxVisibleArea = 0;
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const nextAnchor = sections[index + 1];
        const sectionEnd = nextAnchor ? nextAnchor.getBoundingClientRect().top : lastElementInSectionsParent?.getBoundingClientRect().bottom ?? document.documentElement.scrollHeight;

        // Calculate visible portion of the current section
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(window.innerHeight, sectionEnd);
        const visibleHeight = visibleBottom - visibleTop;

        // Calculate section height (distance to next section)
        const sectionHeight = sectionEnd - rect.top;
        const visibleArea = Math.max(0, visibleHeight / sectionHeight);
        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea;
          mostVisibleAnchor = section.id;
        }
      });
      if (mostVisibleAnchor && mostVisibleAnchor !== activeSectionId) {
        setActiveSectionId(mostVisibleAnchor);
      }
    }
    function handleScrollEnd() {
      scrolling.current = false;
    }
    if ("onscrollend" in window) {
      // If scrollend is supported
      window.addEventListener("scrollend", handleScrollEnd);
    } else {
      // Fallback for browsers that don't support scrollend
      // @ts-expect-error
      window.addEventListener("scroll", () => {
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
        scrollTimeout.current = setTimeout(handleScrollEnd, 250);
      });
    }
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if ("onscrollend" in window) {
        window.removeEventListener("scrollend", handleScrollEnd);
      } else if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [activeSectionId, sections]);
  return activeSectionId;
}